import React, { useState } from "react";
import { Flag, X, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../contexts/I18nContext";
import { toast } from "sonner";

const REASONS_BG = [
  "Спам или реклама",
  "Неподходящо съдържание",
  "Тормоз / обида",
  "Фалшив профил",
  "Измама",
  "Друго",
];
const REASONS_EN = [
  "Spam or promotion",
  "Inappropriate content",
  "Harassment / abuse",
  "Fake profile",
  "Scam / fraud",
  "Other",
];

/**
 * Inline / icon-only report button + modal.
 * Props:
 *  - reportedId (required)
 *  - reportedName (display only)
 *  - context: "profile" | "chat" | "review"
 *  - contextId: optional id
 *  - variant: "icon" | "ghost" | "chip"
 */
export default function ReportButton({ reportedId, reportedName, context = "profile", contextId, variant = "icon", testIdSuffix = "" }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(lang === "bg" ? REASONS_BG[0] : REASONS_EN[0]);
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || user.id === reportedId) return null;

  const submit = async () => {
    if (!reportedId) return;
    setBusy(true);
    try {
      await api.post("/reports", { reported_id: reportedId, reason, details, context, context_id: contextId });
      toast.success(lang === "bg" ? "Сигналът е изпратен" : "Report submitted");
      setOpen(false);
      setDetails("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const reasons = lang === "bg" ? REASONS_BG : REASONS_EN;

  const trigger = variant === "chip" ? (
    <button
      onClick={() => setOpen(true)}
      className="chip-badge amber hover:opacity-100 opacity-70 transition-opacity"
      data-testid={`report-trigger${testIdSuffix ? `-${testIdSuffix}` : ""}`}
    >
      <Flag className="w-3 h-3" /> {lang === "bg" ? "Сигнал" : "Report"}
    </button>
  ) : variant === "ghost" ? (
    <button
      onClick={() => setOpen(true)}
      className="text-xs font-mono uppercase tracking-widest text-white/40 hover:text-red-400 flex items-center gap-1 transition-colors"
      data-testid={`report-trigger${testIdSuffix ? `-${testIdSuffix}` : ""}`}
    >
      <Flag className="w-3 h-3" /> {lang === "bg" ? "Сигнал" : "Report"}
    </button>
  ) : (
    <button
      onClick={() => setOpen(true)}
      className="p-1.5 text-white/40 hover:text-red-400 transition-colors"
      title={lang === "bg" ? "Сигнал" : "Report"}
      data-testid={`report-trigger${testIdSuffix ? `-${testIdSuffix}` : ""}`}
    >
      <Flag className="w-4 h-4" />
    </button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="report-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !busy && setOpen(false)} />
          <div className="relative glass-elevated w-full max-w-md p-6 animate-fade-up" style={{ opacity: 0 }}>
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div className="label-mono">// {lang === "bg" ? "Сигнал" : "Report"}</div>
            <h2 className="font-oswald uppercase text-2xl tracking-tight mt-1">{reportedName || (lang === "bg" ? "Потребител" : "User")}</h2>
            <p className="text-xs text-white/55 font-mono mt-1">
              {lang === "bg" ? "Сигналите се преглеждат от админ екипа." : "Reports are reviewed by the admin team."}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="label-mono block mb-1.5">{lang === "bg" ? "Причина" : "Reason"}</label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
                  data-testid="report-reason"
                >
                  {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-1.5">{lang === "bg" ? "Допълнително (по избор)" : "Details (optional)"}</label>
                <textarea
                  rows={4}
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder={lang === "bg" ? "Опиши какво се случи..." : "Describe what happened..."}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none text-sm"
                  data-testid="report-details"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-secondary px-4 py-2 text-xs">{lang === "bg" ? "Отказ" : "Cancel"}</button>
                <button type="button" onClick={submit} disabled={busy} className="btn-primary px-4 py-2 text-xs flex items-center gap-1.5" data-testid="report-submit">
                  <Flag className="w-3 h-3" /> {busy ? "..." : (lang === "bg" ? "Изпрати" : "Submit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
